import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { IMAGES } from "@/lib/content";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin/dashboard", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    const saved = localStorage.getItem("fhp_admin_saved");
    if (saved) {
      try {
        const { email: e, password: p } = JSON.parse(saved);
        if (e) setEmail(e);
        if (p) setPassword(p);
        setRemember(true);
      } catch {
        /* ignore */
      }
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      if (remember) localStorage.setItem("fhp_admin_saved", JSON.stringify({ email, password }));
      else localStorage.removeItem("fhp_admin_saved");
      toast.success("Welcome back.");
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#0C131D] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-brand-textMuted focus:outline-none focus:ring-2 focus:ring-brand-cyan";

  return (
    <div className="min-h-screen grid place-items-center bg-brand-base px-5" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <img src={IMAGES.logo} alt="The Fish House Project" className="h-16 w-16 object-contain" />
          <h1 className="mt-4 font-display font-extrabold text-2xl text-white">Project Admin</h1>
          <p className="text-brand-textMuted text-sm mt-1">The Fish House Project CMS</p>
        </div>
        <form onSubmit={submit} className="bg-[#0C131D] border border-white/10 rounded-xl p-7" data-testid="admin-login-form">
          {error && (
            <div className="mb-5 bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded-lg px-4 py-3" data-testid="admin-login-error">
              {error}
            </div>
          )}
          <label className="text-sm text-brand-textSec mb-2 block">Email</label>
          <input type="email" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="admin-email-input" placeholder="you@email.com" />
          <label className="text-sm text-brand-textSec mb-2 block mt-4">Password</label>
          <input type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="admin-password-input" placeholder="••••••••" />
          <label className="flex items-center gap-2 mt-4 cursor-pointer select-none">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="h-4 w-4 accent-[#25C4D8]" data-testid="admin-remember-checkbox" />
            <span className="text-sm text-brand-textSec">Remember my details on this device</span>
          </label>
          <button type="submit" disabled={loading} className="btn-primary mt-6 w-full py-3 text-sm disabled:opacity-60" data-testid="admin-login-submit">
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
